/**
 * Repair for a bank whose `bank_floors.position` column is degenerate.
 *
 * `position` is a floor's ordinal within its bank: the API carries it on every
 * bank floor, bidirectional sync hashes it (`models/hash_fields.go`), and
 * LiftCloud maintains it as a dense sequence — deleting a floor renumbers the
 * survivors (`resequenceBankFloorPositions`, `controllers/bank_floor_delete.go`).
 * Unlike `floor_no` and `floor_index` it carries no NOT NULL constraint, nothing
 * populates it on the create path, and the importer defaulted it to 0 when the
 * source had no such column — so an imported bank arrives with every floor
 * sharing position 0 and nothing distinguishes one floor from another.
 *
 * These helpers derive a distinct position from the order the floors are already
 * in. They deliberately do NOT run on their own — a bank's stored ordinals are
 * configuration, so rewriting them silently on page load would change saved data
 * without anyone seeing it. The wizard surfaces the repair and the operator saves
 * it.
 */

/** The floor fields this repair reads and writes. */
export interface PositionedFloor {
  position: number;
  floor_no: number;
  floor_index: number;
}

/**
 * True when the floors cannot be told apart by position: more than one floor,
 * every one carrying the same value.
 *
 * A single-floor bank is never degenerate — one floor needs no distinguishing —
 * and a bank with genuinely distinct positions is left alone whatever those
 * values are, because a non-sequential numbering may be deliberate.
 */
export function hasDegeneratePositions(floors: PositionedFloor[]): boolean {
  if (floors.length < 2) return false;

  return floors.every((floor) => floor.position === floors[0].position);
}

/**
 * Assign each floor a distinct position from its place in the given order.
 *
 * The caller passes the floors in PHYSICAL order (ascending `floor_index`).
 * `startAt` picks the numbering base and defaults to 0 — `0..n-1` — because
 * that is the sequence LiftCloud itself maintains server-side:
 * `resequenceBankFloorPositions` (controllers/bank_floor_delete.go) renumbers
 * a bank's surviving floors `0..n-1` whenever one is deleted. Leaving the
 * default at 1 would look correct until the first floor deletion, at which
 * point the server would silently shift every floor down by one — matching
 * the server's own numbering means a repaired bank is already in the state a
 * delete would leave it in.
 *
 * `startAt` exists because that server-side default is not universal: some
 * adapter protocols and site conventions address a bank's floors 1-based (or
 * from another offset entirely). Pass the base the destination actually
 * expects rather than hardcoding either convention here — this helper stays
 * agnostic and the caller decides.
 *
 * Floors that already have distinct positions are returned untouched: this is a
 * repair for the degenerate case, not a renumbering pass that would overwrite a
 * deliberate mapping.
 */
export function assignSequentialPositions<T extends PositionedFloor>(
  floors: T[],
  startAt = 0,
): T[] {
  if (!hasDegeneratePositions(floors)) return floors;

  return floors.map((floor, index) => ({ ...floor, position: startAt + index }));
}
